import React from 'react';
import { useInfoModal } from '../../../../hooks';
import { Tooltip } from 'react-tooltip';
import styles from './styles.css';
import GroupHighlightSelectField from '../GroupHighlightSelectField/index.jsx';
import { useTranslation } from 'react-i18next';

// Highlighter icon (Material Symbols format_ink_highlighter)
const HighlighterIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#1f1f1f">
		<path d="M80 0v-160h800V0H80Zm504-480L480-584 320-424l103 104 161-160Zm-47-160 103 103 160-159-104-104-159 160Zm-84-29 216 216-189 190q-24 24-56.5 24T367-263l-27 23H140l126-125q-24-24-25-57.5t23-57.5l189-189Zm0 0 187-187q24-24 56.5-24t56.5 24l104 103q24 24 24 56.5T857-640L669-453 453-669Z" />
	</svg>
);

// Tune/settings icon (Material Symbols tune)
const TuneIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="#1f1f1f">
		<path d="M440-120v-240h80v80h320v80H520v80h-80Zm-320-80v-80h240v80H120Zm160-160v-80H120v-80h160v-80h80v240h-80Zm160-80v-80h400v80H440Zm160-160v-240h80v80h160v80H680v80h-80Zm-480-80v-80h400v80H120Z" />
	</svg>
);

export default function DataTablePanel({ highlights, trends, checkboxes = [], groupHighlights }) {
	const { t } = useTranslation();
	const infoOnClick = useInfoModal();
	const hasHighlightOptions = highlights?.options?.length > 0;
	const hasGroupHighlightOptions = groupHighlights?.options?.length > 0;
	const hasExtraCheckboxes = checkboxes?.length > 0;
	const hasHighlightColumnContent = hasHighlightOptions || hasGroupHighlightOptions;
	const hasTrendOptions = trends?.options?.length > 0;
	const hasConfigurationColumnContent = hasTrendOptions || hasExtraCheckboxes;

	if (!hasHighlightColumnContent && !hasConfigurationColumnContent) {
		return null;
	}

	return (
		<div className="idp-settings-advanced advanced-settings">
			{hasHighlightColumnContent ? (
				<div className="idp-settings-advanced__col">
					<div className="idp-settings-advanced__section-title">
						<HighlighterIcon />
						<span className="idp-settings-advanced__heading">{t('Table highlighting:')}</span>
					</div>
					{hasHighlightOptions ? (
						<div className="idp-settings-advanced__highlights">
							<span className="idp-settings-advanced__sub-label">{highlights.header}</span>
							<div className="idp-settings-advanced__highlight-list">
								{highlights.options.map((option, index) => (
									<label
										key={`${option.text.split(' ').join('')}_${index}`}
										className={`idp-advanced-control-row clickable_pointer ${option.class}`}
									>
										<input
											checked={option.checked}
											type="checkbox"
											className="idp-advanced-control"
											onChange={(e) => option.onclick(e)}
										/>
										{option.text}

										{option.info ? (
											<img
												src="/dist/src/img/icons/infoSymbol.png"
												onClick={infoOnClick(option.info)}
												className={`clickable_pointer ${styles.infoIcon}`}
											/>
										) : null}
									</label>
								))}
							</div>
						</div>
					) : null}
					<GroupHighlightSelectField
						groupHighlights={groupHighlights}
						containerClassName={`${styles.advancedSelectField} ${styles.advancedSelectFieldSpaced}`}
						selectClassName={styles.advancedSelect}
						labelClassName="idp-settings-advanced__sub-label"
					/>
				</div>
			) : null}

			{hasConfigurationColumnContent ? (
				<div className="idp-settings-advanced__col">
					<div className="idp-settings-advanced__trends">
						<div className="idp-settings-advanced__section-title">
							<TuneIcon />
							<span className="idp-settings-advanced__heading">{t('Data configuration:')}</span>
						</div>
						{hasTrendOptions ? (
							<div className={`${styles.trendOptions} idp-advanced-option-group idp-settings-advanced__field`}>
								{trends.options.map((option, index) => (
									<div className={styles.trendOption} key={index}>
										<label
											className={`idp-advanced-control-row idp-advanced-radio-row clickable_pointer ${
												option.checked ? 'idp-advanced-radio-row--checked' : ''
											}`}
											key={`${option.text.split(' ').join('')}_${index}`}
										>
											<input
												type="radio"
												className="idp-advanced-control"
												name="table-data-configuration"
												checked={option.checked}
												onChange={() => {
													if (option.checked) {
														return;
													}
													option.onClick();
												}}
											/>
											{option.text}
										</label>
										<span
											className={styles.trendOptionTooltip}
											data-tooltip-id="table-config-tooltip"
											data-tooltip-content={trends.legend[index]}
											aria-label={trends.legend[index]}
											onClick={(e) => e.stopPropagation()}
										>
											<img src="/dist/src/img/icons/infoSymbol.png" alt="" className={styles.trendInfoIcon} />
										</span>

										{option.checked && option.id && option.id === 'significantChanges' ? (
											<div>
												<select
													className={`${styles.advancedSelect} ${styles.dropdown}`}
													value={option.dropdown.selected}
													onChange={(e) => option.dropdown.onChange(e.target.value)}
												>
													{option.dropdown.options.map((opt) => (
														<option key={opt} value={opt}>{`${opt}%`}</option>
													))}
												</select>
											</div>
										) : null}
									</div>
								))}
							</div>
						) : null}
						{checkboxes.map((checkbox, index) => (
							<label
								key={`${checkbox.text.split(' ').join('')}_${index}`}
								className={`idp-advanced-control-row clickable_pointer`}
							>
								<input
									type="checkbox"
									className="idp-advanced-control"
									onChange={(e) => {
										checkbox.onChange(e.target.checked);
									}}
									checked={checkbox.checked}
								/>
								{checkbox.text}
							</label>
						))}
						<Tooltip id="table-config-tooltip" />
					</div>
				</div>
			) : null}
		</div>
	);
}
